import { useState } from "react";
import { Modal } from "./Modal";
import { supabase } from "../hooks/useAuth";
import { validarSenhaForte, SENHA_DICA } from "../lib/senha";
import { mensagemDeErroDeSenha } from "../lib/erroDeSenha";
import { KeyRound, Eye, EyeOff, CheckCircle2 } from "lucide-react";

/**
 * Troca de senha self-service do usuário logado (segurança).
 * Usa supabase.auth.updateUser na sessão ativa. Regra de senha forte
 * compartilhada (lib/senha.ts), espelhada no backend (usuarios.ts).
 */
export function TrocarSenhaModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [s1, setS1] = useState("");
  const [s2, setS2] = useState("");
  const [ver, setVer] = useState(false);
  const [erro, setErro] = useState("");
  const [ok, setOk] = useState(false);
  const [salvando, setSalvando] = useState(false);

  function fechar() {
    setS1("");
    setS2("");
    setErro("");
    setOk(false);
    onClose();
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    setErro("");
    const v = validarSenhaForte(s1);
    if (v) return setErro(v);
    if (s1 !== s2) return setErro("As duas senhas não conferem.");
    setSalvando(true);
    const { error } = await supabase.auth.updateUser({ password: s1 });
    setSalvando(false);
    if (error) return setErro(mensagemDeErroDeSenha(error));
    setOk(true);
    setTimeout(fechar, 1600);
  }

  // Validação AO VIVO (enquanto digita) — não espera o submit.
  const erroSenha = s1 ? validarSenhaForte(s1) : null;
  const naoConferem = s2.length > 0 && s1 !== s2;
  const podeSalvar = !salvando && !!s1 && !!s2 && !erroSenha && s1 === s2;

  return (
    <Modal open={open} onClose={fechar} title="Trocar minha senha">
      <form onSubmit={salvar} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Nova senha</label>
          <div className="relative">
            <input
              type={ver ? "text" : "password"}
              value={s1}
              onChange={(e) => setS1(e.target.value)}
              autoComplete="new-password"
              className="w-full border rounded-md h-10 px-3 pr-10 text-sm bg-background"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setVer((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={ver ? "Ocultar senha" : "Mostrar senha"}
            >
              {ver ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Confirmar nova senha</label>
          <input
            type={ver ? "text" : "password"}
            value={s2}
            onChange={(e) => setS2(e.target.value)}
            autoComplete="new-password"
            className="w-full border rounded-md h-10 px-3 text-sm bg-background"
            placeholder="••••••••"
          />
        </div>

        {erroSenha ? (
          <p className="text-[11px] text-rose-600">{erroSenha}</p>
        ) : s1 ? (
          <p className="text-[11px] text-emerald-600">Senha forte ✓</p>
        ) : (
          <p className="text-[11px] text-muted-foreground">{SENHA_DICA}</p>
        )}
        {naoConferem && <p className="text-[11px] text-rose-600">As duas senhas não conferem.</p>}

        {erro && (
          <div className="text-xs bg-rose-50 text-rose-700 border border-rose-200 rounded-md px-3 py-2 dark:bg-rose-950/30 dark:text-rose-300">
            {erro}
          </div>
        )}
        {ok && (
          <div className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md px-3 py-2 inline-flex items-center gap-1.5 dark:bg-emerald-950/30 dark:text-emerald-300">
            <CheckCircle2 className="h-4 w-4" /> Senha alterada com sucesso!
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={fechar} className="px-3 py-2 rounded-md text-sm border hover:bg-muted">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!podeSalvar}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <KeyRound className="h-4 w-4" /> {salvando ? "Salvando…" : "Salvar nova senha"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
